import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIndexedDB } from "react-indexed-db-hook";
import { v4 as uuidv4 } from "uuid";
import { usePageVisibility } from "react-page-visibility";
import { Box, Container, Spinner } from '../components/legacy-widgets';
import { authClient } from '../utils/amplifyClient';
import { uploadData } from "aws-amplify/storage";
import * as Sentry from "@sentry/browser";
import { useUserContext } from "../contexts/UserContext";
import {
  createDiscrepancy as createDiscrepancyMutation,
  createReport as createReportMutation,
  createErrors as createErrorsMutation,
  createInspectionReport as createInspectionReportMutation,
  createDailyServiceReport as createDailyServiceReportMutation,
  createShiftReport as createShiftReportMutation,
  createReportAnswer as createReportAnswerMutation,
  updateInspectionReport as updateInspectionReportMutation,
  updateDailyServiceReport as updateDailyServiceReportMutation,
  updateDiscrepancy as updateDiscrepancyMutation,
} from "../graphql/mutations";
import { listDiscrepancyByUnitIdAndUpdatedAt } from "../graphql/queries";
import { ModelSortDirection, type CreateReportInput } from "../graphql/API";

import { useNetworkState } from "@uidotdev/usehooks";
import {
  ITEM_STATUS_CODES,
  LOCAL_STORAGE_KEYS,
  REPORT_SENDING_STATE,
  REPORT_TYPES,
  S3_IMAGE_PATH,
  SYSTEM_MESSAGE_STRINGS,
} from "../utils/constants";
import { tInspectionFormInput, tMediaInput } from "../types";
import { QueuedReportPayload, InspectionReportPayload, DailyServiceReportPayload, ShiftReportPayload } from "../types/reportTypes";
import type { SingleUnit } from "../components/pages-equipment/equipmentTypes";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { base64ToBlob, resolveActiveCompanyId } from "../utils/helperFunctions";
import { notifyPMDueSoon } from "../utils/pmDueNotification";
import {
  readCachedSignatureStoragePath,
  readSavedSignatureData,
  resolveSignatureDataFromRecord,
} from "../components/inspection-flow/inspectionSignatureUtils";
import { toLocalCalendarDateString } from "../utils/dateInputUtils";
import { useEquipmentContext } from "../contexts/EquipmentContext";
import { useAppContext } from "../contexts/AppContext";
import { Btn, Stack } from "../components/ui";
import { buildReportPath } from "../utils/reportUrlUtils";
import { hasPermission, Permission } from "../utils/permissions";

/** Survives React Strict Mode remounts so the queue is not processed twice. */
let isReportQueueProcessing = false;
import {
  getReportQueue,
  shiftReportQueue,
  buildCreateShiftReportInput,
  computeDurationSeconds,
  toAwsDateTime,
  isCreateDailyServiceReportInputFieldError,
  isCreateShiftReportInputFieldError,
  isStatusBearing,
  buildPromptKey,
  toLegacyCreateDailyServiceReportInput,
  toLegacyCreateShiftReportInput,
  persistReportSentSummary,
  readReportSentSummary,
  clearReportSentSummary,
  resolveReportUMCounterValue,
  lastServiceReadingBeforeAdvance,
  resolveMaxFiniteUM,
  resolveReportUnitId,
  type ReportSentSummary,
} from "../utils/reportUtils";
import {
  formatOperatorResponseLabel,
  shouldCreateIssueForPrompt,
} from "../utils/formPromptMapping";
import { findMatchingOpenIssue } from "../utils/discrepancyDedupe";
import {
  attachDailyServiceCostSnapshots,
  buildDailyServiceCostSnapshots,
  buildDailyServiceExpenseInput,
  isDailyServiceCostTrackingEnabled,
  isDailyServiceExpenseForReport,
} from "../utils/dailyServiceCostTracking";
import { createExpenseRecord, fetchExpenseRecordsByUnit } from "../graphql/costTrackingQueries";
import {
  listServiceItemsByCompanyId,
  listServiceProcedureConsumablesByCompanyId,
} from "../graphql/companyResourceQueries";
import "../css/ReportSentPage.css";

type LastSubmittedReport = ReportSentSummary['report'];

const getQueueItemReportSlugLabel = (
  item: QueuedReportPayload,
  fallbackEquipmentName: string | null,
): string => {
  if (item.reportType === REPORT_TYPES.SHIFT) {
    const shiftItem = item as ShiftReportPayload;
    return shiftItem.shiftType === "end-of-day" ? "End of day shift report" : "Start of day shift report";
  }
  const unitItem = item as InspectionReportPayload | DailyServiceReportPayload;
  return unitItem.unitName || unitItem.equipmentCategoryName || fallbackEquipmentName || "report";
};

export const ReportSent = () => {
  const navigate = useNavigate();
  const network = useNetworkState();
  const [reportStatus, setReportStatus] = useState(
    REPORT_SENDING_STATE.CHECKING_NETWORK
  );
  const [mediaFilesUploadedCount, setMediaFilesUploadedCount] = useState(0);
  const [queueCount, setQueueCount] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const [lastSubmittedReport, setLastSubmittedReport] = useState<LastSubmittedReport | null>(null);
  const [successEquipmentName, setSuccessEquipmentName] = useState<string | null>(
    () => localStorage.getItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME),
  );
  const { company, companyUser, companyOwner, businessAdmin } = useUserContext();
  const { updateUnitInDatabase, refreshEquipment, companyUnits } = useEquipmentContext();
  const { setIssueCount } = useAppContext();
  const isTabVisible = usePageVisibility();
  const equipmentName = localStorage.getItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME);
  const { deleteRecord: deleteMediaRecord, getByID: getMediaByID } = useIndexedDB("media");
  const { deleteRecord: deleteSignatureRecord, getByID: getSignatureByID } = useIndexedDB("signature");
  const { user } = useAuthenticator((context) => [context.user]);
  const isProcessingRef = useRef(false);

  const companyId =
    resolveActiveCompanyId({ companyUser, companyOwner, company }) ||
    companyOwner?.companyId ||
    "";
  const canViewReports = hasPermission(
    companyOwner,
    companyUser,
    Permission.VIEW_REPORTS,
    businessAdmin,
  );

  // ─── Signature upload per queue item ─────────────────────────────────────────
  const handleSignature = async (item: QueuedReportPayload): Promise<string | null> => {
    try {
      if (!companyId) return null;

      const signatureRecord = await getSignatureByID(companyId);
      let signatureData = resolveSignatureDataFromRecord(signatureRecord);

      if (!signatureData) {
        signatureData = readSavedSignatureData(companyId);
      }

      const cachedPath = readCachedSignatureStoragePath();
      if (!signatureData && cachedPath) {
        return cachedPath;
      }

      if (!signatureData) return null;

      const equipId = (item as InspectionReportPayload).equipmentCategoryId || "general";
      const unitId = item.reportType !== REPORT_TYPES.SHIFT ? (item as InspectionReportPayload).unitId : undefined;
      const imageName = `${equipId}/${unitId ? `${unitId}/` : ""}${uuidv4()}-SIGNATURE-${equipmentName || "report"}.jpeg`;

      const blob = base64ToBlob(signatureData, "image/jpeg") as BlobPart;
      const imageFile = new File([blob], imageName, { type: "image/jpeg" });

      const savedImageS3 = await uploadData({
        path: S3_IMAGE_PATH.signatureImagePath(companyId, imageName),
        data: imageFile,
      }).result;

      const uploadedPath = savedImageS3?.path || null;
      if (uploadedPath) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH, uploadedPath);
      }

      return uploadedPath;
    } catch (error) {
      console.error("Error handling signature:", error);
      Sentry.captureException(error);
      throw error;
    }
  };

  // ─── Upload media for a single queue item ────────────────────────────────────
  const uploadMedia = async (
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[]
  ): Promise<{ updatedPrompts: tInspectionFormInput[]; imagesToDelete: number[] }> => {
    const mediaObj: { [key: number]: tMediaInput[] } | null = JSON.parse(item.media || "null");
    const imagesToDelete: number[] = [];

    if (!mediaObj) return { updatedPrompts: promptArray, imagesToDelete };

    for (const key in mediaObj) {
      const imgArray = mediaObj[key];
      const idx = parseInt(key, 10);
      if (promptArray[idx]) {
        promptArray[idx].photos = [];
      }
      for (const img of imgArray) {
        try {
          const imageFile = await getMediaByID(img.id);
          if (!imageFile) {
            Sentry.captureMessage(`IndexedDB media record not found for id ${img.id}`);
            continue;
          }
          const fileName = `${uuidv4()}-IMAGE-${img.name}`;
          const fullPath = S3_IMAGE_PATH.fullInspectionReportImagePath(companyId, fileName);
          const result = await uploadData({
            path: fullPath,
            data: imageFile.file,
          }).result;
          if (result?.path) {
            setMediaFilesUploadedCount((prev) => prev + 1);
          }
          if (promptArray[idx]) {
            promptArray[idx].photos!.push(fullPath);
          }
          imagesToDelete.push(img.id);
        } catch (error) {
          console.error("Error uploading media:", error);
          Sentry.captureException(error);
          throw error;
        }
      }
    }

    return { updatedPrompts: promptArray, imagesToDelete };
  };

  // ─── Report type routing: select the correct mutation ────────────────────────
  const getCreateMutation = (reportType: string | undefined | null) => {
    switch (reportType) {
      case REPORT_TYPES.INSPECTION:
        return createInspectionReportMutation;
      case REPORT_TYPES.DAILY_SERVICE:
        return createDailyServiceReportMutation;
      case REPORT_TYPES.SHIFT:
        return createShiftReportMutation;
      default:
        return createReportMutation;
    }
  };

  // ─── Build mutation input based on report type ───────────────────────────────
  const buildMutationInput = (
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[],
    signaturePath: string | null,
    userId: string,
    userName: string
  ) => {
    const inspectionCompleted = new Date().toISOString();
    const inspectionStarted = toAwsDateTime(item.inspectionStarted, inspectionCompleted);
    const inspectionDurationSeconds = computeDurationSeconds(inspectionStarted, inspectionCompleted);

    const baseFields = {
      companyId: item.companyId,
      userId,
      formId: item.formId,
      responseData: JSON.stringify(promptArray),
      okCount: item.okCount,
      warnCount: item.warnCount,
      inopCount: item.inopCount,
      totalItems: item.totalItems,
      inspectionDurationSeconds,
      signature: signaturePath,
      photos: promptArray.flatMap((p) => p.photos || []).filter(Boolean),
      gpsLatitude: item.gpsLatitude,
      gpsLongitude: item.gpsLongitude,
      gpsLocationType: item.gpsLocationType,
      inspectionStarted,
      inspectionCompleted,
      createdAt: inspectionCompleted,
    };

    switch (item.reportType) {
      case REPORT_TYPES.INSPECTION: {
        const ir = item as InspectionReportPayload;
        return {
          ...baseFields,
          equipmentCategoryId: ir.equipmentCategoryId,
          unitId: ir.unitId,
          equipmentCategoryName: ir.equipmentCategoryName,
          unitName: ir.unitName,
          userName,
          locationId: ir.locationId,
          machineHoursAtInspection: ir.machineHoursAtInspection,
          umCounterAtInspection: ir.umCounterAtInspection,
          alwaysTriggerEmail: ir.alwaysTriggerEmail,
        };
      }
      case REPORT_TYPES.DAILY_SERVICE: {
        const ds = item as DailyServiceReportPayload;
        return {
          ...baseFields,
          equipmentCategoryId: ds.equipmentCategoryId,
          unitId: ds.unitId,
          equipmentCategoryName: ds.equipmentCategoryName,
          unitName: ds.unitName,
          userName,
          locationId: ds.locationId,
          machineHoursAtService: ds.machineHoursAtService,
          umCounterAtService: ds.umCounterAtService,
          dailyServiceLogFormId: ds.dailyServiceLogFormId,
          alwaysTriggerEmail: ds.alwaysTriggerEmail,
        };
      }
      case REPORT_TYPES.SHIFT: {
        return buildCreateShiftReportInput({
          item: item as ShiftReportPayload,
          promptArray,
          signaturePath,
          userId,
          userName,
          inspectionCompleted,
        });
      }
      default: {
        // Legacy fallback — build a Report-compatible input
        const fallbackItem = item as unknown as QueuedReportPayload & {
          equipmentCategoryId?: string | null;
          equipmentCategoryName?: string | null;
          unitId?: string | null;
        };
        return {
          companyId: fallbackItem.companyId,
          userId,
          name: userName,
          userData: JSON.stringify(promptArray),
          signature: signaturePath,
          equipmentId: fallbackItem.equipmentCategoryId || "",
          equipmentName: fallbackItem.equipmentCategoryName || "",
          unitId: fallbackItem.unitId || "",
          userLocation: JSON.stringify({
            type: fallbackItem.gpsLocationType,
            latitude: fallbackItem.gpsLatitude,
            longitude: fallbackItem.gpsLongitude,
          }),
          inspectionStarted: toAwsDateTime(fallbackItem.inspectionStarted, new Date().toISOString()),
          inspectionCompleted: new Date().toISOString(),
        };
      }
    }
  };

  // ─── Create ReportAnswer records for status-bearing responses ────────────────
  const createReportAnswers = async (
    parentReportId: string,
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[]
  ) => {
    for (let i = 0; i < promptArray.length; i++) {
      const prompt = promptArray[i];
      if (!isStatusBearing(prompt.value)) continue;

      try {
        await authClient.graphql({
          query: createReportAnswerMutation,
          variables: {
            input: {
              parentReportId,
              reportType: item.reportType,
              companyId: item.companyId,
              unitId: item.reportType !== REPORT_TYPES.SHIFT ? (item as InspectionReportPayload).unitId : undefined,
              promptKey: buildPromptKey(prompt),
              promptTitle: prompt.title || null,
              promptLabel: prompt.label || null,
              promptDescription: prompt.description || null,
              promptType: prompt.type || null,
              value: prompt.value,
              notes: prompt.notes || null,
              photos: prompt.photos?.filter(Boolean) as string[] || null,
              sortOrder: i,
            },
          },
        });
      } catch (error) {
        // ReportAnswer failures are logged but do NOT stop queue processing
        console.error(`Failed to create ReportAnswer for prompt ${i}:`, error);
        Sentry.captureException(error);
      }
    }
  };

  // ─── Create Discrepancy records for warn/inop (Inspection + DailyService only)
  const createDiscrepancies = async (
    parentReportId: string,
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[],
    userName: string
  ): Promise<number> => {
    // Only create discrepancies for InspectionReport and DailyServiceReport
    if (item.reportType !== REPORT_TYPES.INSPECTION && item.reportType !== REPORT_TYPES.DAILY_SERVICE) {
      return 0;
    }

    const typedItem = item as InspectionReportPayload | DailyServiceReportPayload;
    let createdCount = 0;

    type OpenIssue = {
      id: string;
      status?: string | null;
      questionIndex?: number | null;
      promptTitle?: string | null;
      promptLabel?: string | null;
      promptDescription?: string | null;
      log?: string | null;
    };

    let existingOpen: OpenIssue[] = [];
    if (typedItem.unitId) {
      try {
        const existingResponse = await authClient.graphql({
          query: listDiscrepancyByUnitIdAndUpdatedAt,
          variables: {
            unitId: typedItem.unitId,
            sortDirection: ModelSortDirection.DESC,
            limit: 200,
          },
        });
        existingOpen = (existingResponse.data.listDiscrepancyByUnitIdAndUpdatedAt.items || []).filter(
          (issue) => issue && issue.status !== ITEM_STATUS_CODES.ok,
        ) as OpenIssue[];
      } catch (error) {
        console.error('Failed to load existing discrepancies for dedupe:', error);
        Sentry.captureException(error);
      }
    }

    const findMatchingOpenIssueForPrompt = (prompt: tInspectionFormInput, questionIndex: number) =>
      findMatchingOpenIssue(existingOpen, prompt, questionIndex);

    for (let i = 0; i < promptArray.length; i++) {
      const prompt = promptArray[i];
      if (!shouldCreateIssueForPrompt(prompt)) continue;

      // Non-status autoFlag triggers still land as warn tickets so Issues UI works.
      const discrepancyStatus =
        prompt.value === ITEM_STATUS_CODES.warn || prompt.value === ITEM_STATUS_CODES.inop
          ? prompt.value
          : ITEM_STATUS_CODES.warn;
      const responseLabel = formatOperatorResponseLabel(prompt.value);
      const issueNotes =
        prompt.notes?.trim() ||
        (discrepancyStatus !== prompt.value
          ? `Auto-flagged: ${responseLabel}${prompt.defaultUM ? ` (${prompt.defaultUM})` : ''}`
          : prompt.notes);

      const matchingOpen = findMatchingOpenIssueForPrompt(prompt, i);
      if (matchingOpen) {
        // Reuse the open ticket instead of minting a second ID for the same prompt.
        try {
          let priorLog: unknown[] = [];
          try {
            priorLog = JSON.parse(matchingOpen.log || '[]');
            if (!Array.isArray(priorLog)) priorLog = [];
          } catch {
            priorLog = [];
          }

          await authClient.graphql({
            query: updateDiscrepancyMutation,
            variables: {
              input: {
                id: matchingOpen.id,
                status: discrepancyStatus,
                reportId: parentReportId,
                log: JSON.stringify([
                  ...priorLog,
                  {
                    name: userName,
                    notes: issueNotes || 'Re-flagged on a new inspection',
                    media: prompt.photos,
                    date: new Date().toISOString(),
                    source: item.reportType,
                  },
                ]),
              },
            },
          });
        } catch (error) {
          console.error(`Failed to update existing Discrepancy for prompt ${i}:`, error);
          Sentry.captureException(error);
        }
        continue;
      }

      try {
        const created = await authClient.graphql({
          query: createDiscrepancyMutation,
          variables: {
            input: {
              companyId: item.companyId,
              equipmentId: typedItem.equipmentCategoryId,
              reportId: parentReportId,
              equipmentName: typedItem.equipmentCategoryName,
              questionIndex: i,
              unitId: typedItem.unitId,
              status: discrepancyStatus,
              initialStatus: discrepancyStatus,
              log: JSON.stringify([
                {
                  name: userName,
                  notes: issueNotes,
                  media: prompt.photos,
                  date: new Date().toISOString(),
                  source: item.reportType,
                },
              ]),
              promptTitle: prompt.title,
              promptLabel: prompt.label,
              promptDescription: prompt.description,
            },
          },
        });
        const createdId = created.data?.createDiscrepancy?.id;
        if (createdId) {
          existingOpen.push({
            id: createdId,
            status: discrepancyStatus,
            questionIndex: i,
            promptTitle: prompt.title,
            promptLabel: prompt.label,
            promptDescription: prompt.description,
          });
        }
        createdCount++;
      } catch (error) {
        // Discrepancy failures are logged but do NOT stop queue processing
        console.error(`Failed to create Discrepancy for prompt ${i}:`, error);
        Sentry.captureException(error);
      }
    }

    return createdCount;
  };

  const createDailyServiceExpense = async (
    parentReportId: string,
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[],
  ) => {
    if (item.reportType !== REPORT_TYPES.DAILY_SERVICE) return;
    const ds = item as DailyServiceReportPayload;
    if (!ds.unitId || !ds.companyId || ds.companyId !== companyId) return;

    const unit = companyUnits.find((listedUnit) => listedUnit.id === ds.unitId);
    if (!isDailyServiceCostTrackingEnabled(company, unit)) return;

    try {
      const [catalog, links] = await Promise.all([
        listServiceItemsByCompanyId(ds.companyId),
        listServiceProcedureConsumablesByCompanyId(ds.companyId),
      ]);
      // Gen2 catalog rows satisfy the fields this util reads; cast at the boundary
      // until pmTypes.PMPartsAndProcedure is migrated to the schema type (GEN2-TYPE-MIGRATION-PLAN).
      const pricedPrompts = attachDailyServiceCostSnapshots(
        promptArray,
        catalog as unknown as Parameters<typeof attachDailyServiceCostSnapshots>[1],
        links,
      );
      const snapshots = buildDailyServiceCostSnapshots(pricedPrompts);
      const expenseInput = buildDailyServiceExpenseInput({
        companyId: ds.companyId,
        unitId: ds.unitId,
        equipmentCategoryId: ds.equipmentCategoryId,
        reportId: parentReportId,
        unitName: ds.unitName,
        dateOfService: new Date().toISOString(),
        snapshots,
      });
      if (!expenseInput) return;

      const existing = await fetchExpenseRecordsByUnit(ds.unitId);
      const alreadyRecorded = (existing || []).some((expense) =>
        isDailyServiceExpenseForReport(expense || {}, parentReportId),
      );
      if (alreadyRecorded) return;
      await createExpenseRecord(expenseInput);
    } catch (error) {
      console.error('Failed to record Daily Service Cost Tracking expense:', error);
      Sentry.captureException(error);
    }
  };

  // ─── Ensure GSI sort key exists so Reports tab queries find this row ─────────
  const ensureReportListIndex = async (
    item: QueuedReportPayload,
    reportId: string,
    createdAt: string,
    createdReport: { createdAt?: string | null },
  ) => {
    if (createdReport?.createdAt) {
      return;
    }
    try {
      if (item.reportType === REPORT_TYPES.INSPECTION) {
        await authClient.graphql({
          query: updateInspectionReportMutation,
          variables: { input: { id: reportId, createdAt } },
        });
      } else if (item.reportType === REPORT_TYPES.DAILY_SERVICE) {
        await authClient.graphql({
          query: updateDailyServiceReportMutation,
          variables: { input: { id: reportId, createdAt } },
        });
      }
    } catch (error) {
      console.error("Failed to backfill report createdAt for list index:", error);
      Sentry.captureException(error);
    }
  };

type CreatedReportSnapshot = {
  id?: string | null;
  createdAt?: string | null;
  unitId?: string | null;
  umCounterAtInspection?: number | null;
  machineHoursAtInspection?: number | null;
  umCounterAtService?: number | null;
  machineHoursAtService?: number | null;
};

  // ─── Update SingleUnit counters (Inspection + DailyService only) ─────────────
  const updateUnitCounters = async (
    item: QueuedReportPayload,
    promptArray: tInspectionFormInput[],
    createdReport?: CreatedReportSnapshot | null,
  ) => {
    if (item.reportType !== REPORT_TYPES.INSPECTION && item.reportType !== REPORT_TYPES.DAILY_SERVICE) {
      return;
    }

    const typedItem = item as InspectionReportPayload | DailyServiceReportPayload;
    const unitId = resolveReportUnitId(typedItem, createdReport ?? undefined);
    if (!unitId) {
      console.warn("ReportSent: skipping unit counter update — no unitId on report payload.");
      return;
    }

    try {
      const typedInspection = item as InspectionReportPayload;
      const typedDaily = item as DailyServiceReportPayload;
      const payloadCounter =
        item.reportType === REPORT_TYPES.INSPECTION
          ? typedInspection.umCounterAtInspection ?? typedInspection.machineHoursAtInspection
          : typedDaily.umCounterAtService ?? typedDaily.machineHoursAtService;

      const savedCounter =
        item.reportType === REPORT_TYPES.INSPECTION
          ? createdReport?.umCounterAtInspection ?? createdReport?.machineHoursAtInspection
          : createdReport?.umCounterAtService ?? createdReport?.machineHoursAtService;

      const machineHoursValue = resolveReportUMCounterValue(
        promptArray,
        payloadCounter ?? savedCounter ?? null,
      );
      const updates: Partial<SingleUnit> = {};

      if (machineHoursValue != null && Number.isFinite(machineHoursValue)) {
        // max(Inspection, DS): never let a lower reading from one flow erase a higher one
        const existingUnit = companyUnits.find((listed) => listed.id === unitId);
        const previousFace = existingUnit?.currentUMCounterValue;
        const ratcheted = resolveMaxFiniteUM(
          machineHoursValue,
          previousFace,
        );
        if (ratcheted != null) {
          updates.currentUMCounterValue = ratcheted;
        }
        const lastService = lastServiceReadingBeforeAdvance(previousFace, machineHoursValue);
        if (lastService != null) {
          updates.lastServiceHours = lastService;
        }
      }

      if (item.reportType === REPORT_TYPES.INSPECTION) {
        updates.lastInspectionDate = toLocalCalendarDateString();
      } else if (item.reportType === REPORT_TYPES.DAILY_SERVICE) {
        updates.lastDailyServiceDate = toLocalCalendarDateString();
      }

      if (Object.keys(updates).length > 0) {
        await updateUnitInDatabase(unitId, updates);
        await refreshEquipment();
        if (updates.currentUMCounterValue != null) {
          try {
            await notifyPMDueSoon(unitId, updates.currentUMCounterValue);
          } catch (noticeError) {
            console.error("Failed to send PM due notice:", noticeError);
            Sentry.captureException(noticeError);
          }
        }
      }
    } catch (error) {
      console.error("Failed to update unit counters:", error);
      Sentry.captureException(error);
    }
  };

  // ─── Process a single queue item ─────────────────────────────────────────────
  const processQueueItem = async (item: QueuedReportPayload): Promise<LastSubmittedReport> => {
    const userId = companyUser?.id || companyOwner?.id || user.userId;
    const userName = companyUser?.name || companyOwner?.name || user.username;

    // 1. Upload signature
    const signaturePath = await handleSignature(item);

    // 2. Upload media and update prompt photos
    const promptArray: tInspectionFormInput[] = JSON.parse(item.responseData);
    const { updatedPrompts, imagesToDelete } = await uploadMedia(item, promptArray);

    // 3. Build mutation input
    const mutationInput = buildMutationInput(item, updatedPrompts, signaturePath, userId, userName);

    // 4. Route to correct mutation
    const mutation = getCreateMutation(item.reportType);

    const runCreateMutation = async (input: Record<string, unknown>) =>
      authClient.graphql({
        query: mutation,
        // `input` is built dynamically to match the report-type-routed mutation and is
        // validated at runtime via the responseErrors handling below. Bridge the loose
        // Record to the create-input contract without altering the payload.
        variables: { input: input as unknown as CreateReportInput },
      });

    let response;
    try {
      response = await runCreateMutation(mutationInput);
      const responseErrors = (response as { errors?: Array<{ message?: string | null }> }).errors;
      if (responseErrors?.length) {
        if (item.reportType === REPORT_TYPES.SHIFT && isCreateShiftReportInputFieldError({ errors: responseErrors })) {
          response = await runCreateMutation(toLegacyCreateShiftReportInput(mutationInput));
        } else if (
          item.reportType === REPORT_TYPES.DAILY_SERVICE &&
          isCreateDailyServiceReportInputFieldError({ errors: responseErrors })
        ) {
          response = await runCreateMutation(toLegacyCreateDailyServiceReportInput(mutationInput));
        } else {
          throw { errors: responseErrors };
        }
      }
    } catch (error) {
      if (item.reportType === REPORT_TYPES.SHIFT && isCreateShiftReportInputFieldError(error)) {
        response = await runCreateMutation(toLegacyCreateShiftReportInput(mutationInput));
      } else if (item.reportType === REPORT_TYPES.DAILY_SERVICE && isCreateDailyServiceReportInputFieldError(error)) {
        response = await runCreateMutation(toLegacyCreateDailyServiceReportInput(mutationInput));
      } else {
        throw error;
      }
    }

    // 5. Extract the created report ID from the response
    const responseData = (response as {
      data?: {
        createInspectionReport?: CreatedReportSnapshot | null;
        createDailyServiceReport?: CreatedReportSnapshot | null;
        createShiftReport?: CreatedReportSnapshot | null;
        createReport?: CreatedReportSnapshot | null;
      } | null;
    } | null)?.data;
    const createdReport =
      responseData?.createInspectionReport ||
      responseData?.createDailyServiceReport ||
      responseData?.createShiftReport ||
      responseData?.createReport;
    const parentReportId = createdReport?.id;

    if (!parentReportId) {
      throw new Error("No report ID returned from mutation");
    }

    const listIndexTimestamp =
      (mutationInput as { createdAt?: string; inspectionCompleted?: string }).createdAt ||
      (mutationInput as { inspectionCompleted?: string }).inspectionCompleted ||
      new Date().toISOString();

    await ensureReportListIndex(item, parentReportId, listIndexTimestamp, createdReport ?? {});

    // Update unit counters before optional child records so utilization always advances.
    await updateUnitCounters(item, updatedPrompts, createdReport);

    // 6. Create ReportAnswer records for status-bearing responses
    await createReportAnswers(parentReportId, item, updatedPrompts);

    // 7. Create Discrepancy records (Inspection + DailyService only)
    const discrepancyCount = await createDiscrepancies(parentReportId, item, updatedPrompts, userName);
    if (discrepancyCount > 0) {
      setIssueCount(prev => prev + discrepancyCount);
    }

    await createDailyServiceExpense(parentReportId, item, updatedPrompts);

    // 9. Clean up IndexedDB media/signature records
    for (const id of imagesToDelete) {
      await deleteMediaRecord(id);
    }
    if (companyId) {
      await deleteSignatureRecord(companyId);
    }

    // 10. Remove processed item from queue
    shiftReportQueue();

    return {
      id: parentReportId,
      source: item.reportType,
      slugLabel: getQueueItemReportSlugLabel(item, equipmentName),
    };
  };

  // ─── Process the entire queue sequentially ───────────────────────────────────
  const processQueue = async () => {
    if (isReportQueueProcessing) {
      return;
    }
    isReportQueueProcessing = true;

    try {
    if (!companyUser && !companyOwner) {
      Sentry.captureException("No companyUser and no stored Company Id - ReportSent.tsx processQueue");
      setReportStatus(`${REPORT_SENDING_STATE.REPORT_ERROR} Error: No Authenticated User`);
      return;
    }

    const queue = getReportQueue();
    if (queue.length === 0) {
      const summary = readReportSentSummary();
      if (summary) {
        applyRestoredSuccessState(summary);
      }
      return;
    }

    const batchSize = queue.length;
    setTotalToProcess(batchSize);
    setProcessedCount(0);
    setLastSubmittedReport(null);
    setReportStatus(REPORT_SENDING_STATE.REPORT_SENDING);

    let latestSubmittedReport: LastSubmittedReport | null = null;

    // Process items in FIFO order
    // Intentional drain loop: exits via the queue-empty break below.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const currentQueue = getReportQueue();
      if (currentQueue.length === 0) break;

      const currentItem = currentQueue[0];
      try {
        latestSubmittedReport = await processQueueItem(currentItem);
        setLastSubmittedReport(latestSubmittedReport);
        setProcessedCount((prev) => prev + 1);
      } catch (error) {
        // Stop processing on failure, retain failed item and all subsequent items
        console.error("Error processing queue item:", error);
        Sentry.captureException(error);
        setReportStatus(`${REPORT_SENDING_STATE.REPORT_ERROR} ${JSON.stringify(error)}`);
        await authClient.graphql({
          query: createErrorsMutation,
          variables: { input: { errors: JSON.stringify(error, null, 2) } },
        });
        return; // Stop — failed item stays in queue
      }
    }

    // All items processed successfully
    setAllDone(true);
    setReportStatus(REPORT_SENDING_STATE.REPORT_SENT);

    if (latestSubmittedReport) {
      persistReportSentSummary({
        report: latestSubmittedReport,
        equipmentName,
        totalCount: batchSize,
        completedAt: new Date().toISOString(),
      });
      setSuccessEquipmentName(equipmentName);
    }

    // Clean up localStorage keys
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SIGNATURE_PATH);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.UNIT_ID);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.EQUIPMENT_NAME);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.TIME_INSPECTION_STARTED);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SELECTED_UNIT);
    } finally {
      isReportQueueProcessing = false;
    }
  };

  const applyRestoredSuccessState = (summary: ReportSentSummary) => {
    setLastSubmittedReport(summary.report);
    setTotalToProcess(summary.totalCount);
    setSuccessEquipmentName(summary.equipmentName);
    setAllDone(true);
    setReportStatus(REPORT_SENDING_STATE.REPORT_SENT);
  };

  // ─── Restore success UI after refresh (queue already drained) ─────────────────
  useEffect(() => {
    const queue = getReportQueue();
    setQueueCount(queue.length);

    if (queue.length > 0) {
      return;
    }

    const summary = readReportSentSummary();
    if (summary) {
      applyRestoredSuccessState(summary);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  // ─── useEffect: trigger queue processing on network/visibility/user ready ─────
  useEffect(() => {
    const handleNetworkChange = async () => {
      if (isProcessingRef.current || isReportQueueProcessing) return;

      const queue = getReportQueue();
      setQueueCount(queue.length);

      if (!network.online) {
        setReportStatus(REPORT_SENDING_STATE.OFFLINE);
        return;
      }

      if (
        (companyUser?.name || companyOwner?.name) &&
        isTabVisible &&
        queue.length > 0 &&
        reportStatus !== REPORT_SENDING_STATE.REPORT_SENDING
      ) {
        isProcessingRef.current = true;
        try {
          await processQueue();
        } finally {
          isProcessingRef.current = false;
        }
      }
    };

    handleNetworkChange();
  }, [network.online, isTabVisible, companyUser?.name, companyOwner?.name]);

  return (
    <div className="page-container report-sent-page">
      <Stack direction="vertical" size="l">
        <Container className="report-sent-panel">
        {reportStatus === REPORT_SENDING_STATE.CHECKING_NETWORK && queueCount > 0 && (
          <>
            <Box variant="h1">{REPORT_SENDING_STATE.CHECKING_NETWORK}</Box>
            <Box variant="h2">{successEquipmentName || equipmentName}</Box>
            <Box variant="p">
              <Spinner /> Preparing to send…
            </Box>
          </>
        )}

        {/* Offline: show queue count */}
        {reportStatus === REPORT_SENDING_STATE.OFFLINE && !allDone && (
          <>
            <Box variant="h1">{REPORT_SENDING_STATE.OFFLINE}</Box>
            <Box variant="h2">{equipmentName}</Box>
            {queueCount > 0 && (
              <Box variant="p">
                {queueCount} report{queueCount !== 1 ? "s" : ""} queued. Reports will be sent when connectivity returns.
              </Box>
            )}
            <Box variant="p">Stay on this page until your reports finish sending.</Box>
          </>
        )}

        {/* Sending: show progress */}
        {reportStatus === REPORT_SENDING_STATE.REPORT_SENDING && (
          <>
            <Box variant="h1">{REPORT_SENDING_STATE.REPORT_SENDING}</Box>
            <Box variant="h2">{equipmentName}</Box>
            <Box variant="p">
              <Spinner /> {SYSTEM_MESSAGE_STRINGS.LOADING}
            </Box>
            {totalToProcess > 1 && (
              <Box variant="p">
                Processing report {processedCount + 1} of {totalToProcess}...
              </Box>
            )}
            <Box variant="p">Please stay on this screen</Box>
          </>
        )}

        {/* All done */}
        {reportStatus === REPORT_SENDING_STATE.REPORT_SENT && allDone && (
          <>
            <Box variant="h1">All Reports Sent</Box>
            <Box variant="h2">{successEquipmentName || equipmentName || "Report"}</Box>
            <Box variant="p">
              {totalToProcess > 1
                ? `All ${totalToProcess} reports have been sent successfully.`
                : "Your report has been sent successfully."}
            </Box>
            {mediaFilesUploadedCount > 0 && (
              <Box variant="p">Media files uploaded: {mediaFilesUploadedCount}</Box>
            )}
            <Box variant="p">
              {canViewReports && lastSubmittedReport
                ? "Your report is saved. Open it below or return home."
                : "Your report is saved. You can return home when you're ready."}
            </Box>
            <div className="report-sent-actions">
              {canViewReports && lastSubmittedReport && (
                <Btn
                  variant="primary"
                  buttonText={totalToProcess > 1 ? "View latest report" : "View Report"}
                  buttonOnClick={() => {
                    clearReportSentSummary();
                    navigate(
                      buildReportPath({
                        id: lastSubmittedReport.id,
                        source: lastSubmittedReport.source,
                        slugLabel: lastSubmittedReport.slugLabel,
                      }),
                    );
                  }}
                />
              )}
              <Btn
                variant="secondary"
                buttonText="Back to home"
                buttonOnClick={() => {
                  clearReportSentSummary();
                  navigate("/");
                }}
              />
            </div>
          </>
        )}

        {/* Error */}
        {reportStatus.startsWith(REPORT_SENDING_STATE.REPORT_ERROR) && (
          <>
            <Box variant="h1">Error Sending Report</Box>
            <Box variant="h2">{equipmentName}</Box>
            <Box variant="p">{reportStatus}</Box>
            {queueCount > 0 && (
              <Box variant="p">
                {getReportQueue().length} report{getReportQueue().length !== 1 ? "s" : ""} remaining in queue. They will be retried when connectivity is stable.
              </Box>
            )}
          </>
        )}
        </Container>
      </Stack>
    </div>
  );
};
